import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { BlockUi, Box, Container, ThemeProvider, Typography } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';
import { usePendo } from '@av/pendo';

import { useAppContext } from '@/context';
import { JOURNEY_LOGIN, JOURNEY_EPRO, isFeatureEnabled } from '@/global';
import { useQueryParams } from '@/hooks';
import {
  EmailVerification,
  EProAccountLinking,
  ForgotPassword,
  ForgotUserId,
  InboundSSO,
  Login,
  LoginDev,
  Logout,
  NextSteps,
  Registration,
  SecurityQuestions,
  TwoFAChallenge,
  TwoFAEnrollment,
  TwoFAManage,
  UpdatePassword,
  UserAgreement,
  OAA,
  PageTest,
} from '@/journeys';
import { Routing, ErrorPage } from '@/pages';

const specificEnv = getSpecificEnv();

// handle injection of HUMAN script at App.js level
const humanScript = document.createElement('script');
humanScript.src =
  specificEnv === 'prod'
    ? '//client.px-cloud.net/PX2yes24ut/main.min.js'
    : '//client.px-cloud.net/PXbE3Rqbur/main.min.js';
humanScript.async = true;

document.body.appendChild(humanScript);

const App = () => {
  const queryParams = useQueryParams();
  const { loading, setQueryParams } = useAppContext();
  usePendo('');

  useEffect(() => {
    // handle query param parsing and store relevant values in context
    setQueryParams({ ...queryParams });
  }, []);

  return (
    <ThemeProvider>
      <BlockUi
        blocking={loading}
        message={
          <Typography variant="subtitle1">
            <Box sx={{ fontWeight: 'bold' }}>Loading...</Box>
          </Typography>
        }
      >
        <Container data-testid="availity-fr-ui-container" maxWidth={false} disableGutters sx={{ height: '100vh' }}>
          <Routes>
            <Route key="2fa-challenge" path="/2fa-challenge" element={<TwoFAChallenge />} />
            <Route key="2fa-enrollment" path="/2fa-enrollment" element={<TwoFAEnrollment />} />
            <Route key="2fa-management" path="/2fa-management" element={<TwoFAManage />} />
            <Route key="email-verification" path="/email-verification" element={<EmailVerification />} />
            <Route
              key="epro-account-linking"
              path="/epro-account-linking"
              element={<EProAccountLinking journeyConstant={JOURNEY_EPRO} />}
            />
            {isFeatureEnabled('ON-9739') && (
              <Route
                key="error-test"
                path="/error-test"
                element={<ErrorPage errorCode={queryParams?.errorCode} transactionId={queryParams?.transactionId} />}
              />
            )}
            <Route key="forgot-password" path="/forgot-password" element={<ForgotPassword />} />
            <Route key="forgot-userid" path="/forgot-userid" element={<ForgotUserId />} />
            <Route key="login" path="/login" element={<Login journeyConstant={JOURNEY_LOGIN} />} />
            <Route key="logout" path="/logout" element={<Logout />} />
            <Route key="next-steps" path="/next-steps" element={<NextSteps />} />
            <Route key="oaa" path="/oaa" element={<OAA />} />
            <Route key="redirect" path="/redirect" element={<Logout type="redirect" />} />
            <Route key="routing" path="/routing" element={<Routing />} />
            <Route key="saml-inbound" path="/saml-inbound" element={<InboundSSO />} />
            <Route key="security-questions" path="/security-questions" element={<SecurityQuestions />} />
            <Route key="sign-up" path="/sign-up" element={<Registration />} />
            <Route key="update-password" path="/update-password" element={<UpdatePassword />} />
            <Route key="user-agreement" path="/user-agreement" element={<UserAgreement />} />
            {specificEnv !== 'prod' && <Route key="page-test" path="/page-test" element={<PageTest />} />}
            {specificEnv !== 'prod' && <Route key="login-dev" path="/login-dev" element={<LoginDev />} />}
            <Route key="default" path="*" element={<Navigate to="/routing" replace />} />
          </Routes>
        </Container>
      </BlockUi>
    </ThemeProvider>
  );
};

export default App;
